/**
 * Login Streak Tracking Service
 * Tracks user login streaks and awards XP for milestones
 */

import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from './config';
import { awardXP, isTaskCompleted } from './gamification';

interface LoginStreakData {
  userId: string;
  currentStreak: number;
  longestStreak: number;
  lastLoginDate: Date;
  loginDates: string[]; // Array of date strings (YYYY-MM-DD)
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Track user login and update streak
 */
export async function trackLogin(userId: string): Promise<void> {
  try {
    const streakRef = doc(db, 'loginStreaks', userId);
    const streakDoc = await getDoc(streakRef);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayString = today.toISOString().split('T')[0];
    
    if (streakDoc.exists()) {
      const data = streakDoc.data() as LoginStreakData;
      const lastLogin = data.lastLoginDate instanceof Date 
        ? data.lastLoginDate 
        : (data.lastLoginDate as any).toDate();
      
      lastLogin.setHours(0, 0, 0, 0);
      const lastLoginString = lastLogin.toISOString().split('T')[0];
      
      // Check if already logged in today
      if (lastLoginString === todayString) {
        return; // Already tracked today
      }
      
      // Calculate days difference
      const daysDiff = Math.floor((today.getTime() - lastLogin.getTime()) / (1000 * 60 * 60 * 24));
      
      let newStreak = data.currentStreak;
      if (daysDiff === 1) {
        // Consecutive day - increment streak
        newStreak = data.currentStreak + 1;
      } else if (daysDiff > 1) {
        // Streak broken - reset to 1
        newStreak = 1;
      }
      
      const longestStreak = Math.max(newStreak, data.longestStreak);
      const loginDates = [...(data.loginDates || []), todayString];
      
      // Update streak data
      await setDoc(streakRef, {
        userId,
        currentStreak: newStreak,
        longestStreak,
        lastLoginDate: Timestamp.fromDate(today),
        loginDates,
        createdAt: data.createdAt,
        updatedAt: Timestamp.now(),
      });
      
      // Award XP for streak milestones
      await checkAndAwardStreakXP(userId, newStreak);
      
    } else {
      // First login - create streak data
      await setDoc(streakRef, {
        userId,
        currentStreak: 1,
        longestStreak: 1,
        lastLoginDate: Timestamp.fromDate(today),
        loginDates: [todayString],
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
    }
  } catch (error) {
    console.error('[trackLogin] Error:', error);
  }
}

/**
 * Check and award XP for login streak milestones
 */
async function checkAndAwardStreakXP(userId: string, currentStreak: number): Promise<void> {
  try {
    // Award XP for 3-day streak
    if (currentStreak >= 3) {
      const alreadyCompleted3 = await isTaskCompleted(userId, 'login_3_days_streak');
      if (!alreadyCompleted3) {
        await awardXP(userId, 'login_3_days_streak', 30);
        // eslint-disable-next-line no-console
        console.log('[Gamification] Awarded 30 XP for 3-day login streak');
        window.dispatchEvent(new CustomEvent('gamificationUpdate'));
      }
    }
    
    // Award XP for 5-day streak
    if (currentStreak >= 5) {
      const alreadyCompleted5 = await isTaskCompleted(userId, 'login_5_days_streak');
      if (!alreadyCompleted5) {
        await awardXP(userId, 'login_5_days_streak', 50);
        // eslint-disable-next-line no-console
        console.log('[Gamification] Awarded 50 XP for 5-day login streak');
        window.dispatchEvent(new CustomEvent('gamificationUpdate'));
      }
    }
  } catch (error) {
    console.error('[checkAndAwardStreakXP] Error:', error);
  }
}

/**
 * Get user's current login streak
 */
export async function getLoginStreak(userId: string): Promise<LoginStreakData | null> {
  try {
    const streakRef = doc(db, 'loginStreaks', userId);
    const streakDoc = await getDoc(streakRef);
    
    if (streakDoc.exists()) {
      const data = streakDoc.data();
      return {
        userId: data.userId,
        currentStreak: data.currentStreak,
        longestStreak: data.longestStreak,
        lastLoginDate: data.lastLoginDate.toDate(),
        loginDates: data.loginDates || [],
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
      };
    }
    
    return null;
  } catch (error) {
    console.error('[getLoginStreak] Error:', error);
    return null;
  }
}
